import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { AppDeployment, HostingQueryParamToHostingType } from '@atlassian/mpac-types';
import { MarketplaceAppPaymentModel } from '@atlassian/mpac-types/graphql-gateway-types';
import React from 'react';
import { LazySuspense } from 'react-loosely-lazy';
import { AppListingTab, TEST_ID } from '../../constants';
import { appSupportsEditions } from '../../utils';
import { getTrustTabPrecondition, pickVersionInfo } from './AppListing.utils';
import { LoadingSuspenseSpinner } from './Loading';
import { AppListingTabContentSkeleton } from './Skeleton';
import { LazyCustomerPrivacyAndSecurityTab, LazyPrivacySecurityDetailsNotAvailableBanner, LazyPrivacySecurityDetailsNotAvailableBannerV2 } from './tabs/CustomerPrivacyAndSecurityTab';
import InstallationTab from './tabs/InstallationTab';
import LasyInstallationTab from './tabs/InstallationTab/InstallationTabV2';
import { LazyOverviewTab, LazyOverviewTabV2 } from './tabs/OverviewTab';
import LazyPricingTab from './tabs/PricingTab';
import LazyPricingTabV2 from './tabs/PricingTab/PricingTabV2';
import { LazyReviewsTab, LazyReviewsTabV2 } from './tabs/ReviewsTab';
import SupportTab from './tabs/SupportTab';
import { SupportTab as SupportTabV2 } from './tabs/SupportTab/v2/SupportTab';
import VersionsTab from './tabs/VersionsTab';
import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function -- function is thin on logic
export const makeAppListingTabs = _ref => {
  let {
    /* eslint sort-keys: "error" */
    appListing,
    editions,
    selectedHosting,
    showAetherAppListingPage,
    showInstallationTab,
    serverEolStartDate,
    showIntegrationsDetailsSection,
    showResourcesSection,
    showCertificationSection,
    showReviewsTab,
    showVersionsTab,
    showPricingTab,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    user,
    loginHref,
    onSupportLinkClick,
    onMediaViewerOpen,
    onWatchAppToggle,
    onSeeMoreReviewsClick,
    onArtifactDownloadClick,
    cloudMigration,
    onCloudMigrationResourceLinksClick,
    onPricingCalculatorInstallCTAClick,
    showAppListingTrustTab,
    appRating,
    showServerEolMessage,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
    isServerAppEos,
    onTabSelect,
    appPricingDetailsLink
  } = _ref;
  const tabs = [];
  const trustTabPrecondition = getTrustTabPrecondition(selectedHosting, appListing);
  const isEditionsSupported = appSupportsEditions(editions);

  // Overview Tab
  tabs.push({
    label: 'Overview',
    content: showAetherAppListingPage ? /*#__PURE__*/_jsx(LazySuspense, {
      fallback: /*#__PURE__*/_jsx(AppListingTabContentSkeleton, {}),
      children: /*#__PURE__*/_jsx(LazyOverviewTabV2, {
        appListing: appListing,
        isEditionsSupported: isEditionsSupported,
        editions: editions,
        appPricingDetailsLink: appPricingDetailsLink,
        hosting: selectedHosting,
        isServerAppEos: isServerAppEos,
        mpacBaseUrl: mpacBaseUrl,
        onArtifactDownloadClick: onArtifactDownloadClick,
        onMediaViewerOpen: onMediaViewerOpen,
        onTabSelect: onTabSelect,
        showIntegrationsDetailsSection: showIntegrationsDetailsSection,
        versionInfo: pickVersionInfo(appListing)
      })
    }) : /*#__PURE__*/_jsx(LazySuspense, {
      fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
      children: /*#__PURE__*/_jsx(LazyOverviewTab, {
        mpacBaseUrl: mpacBaseUrl,
        appListing: appListing,
        cloudMigration: cloudMigration,
        showIntegrationsDetailsSection: showIntegrationsDetailsSection,
        showResourcesSection: showResourcesSection,
        showCertificationSection: showCertificationSection,
        userLoggedIn: user.isLoggedIn,
        loginHref: loginHref,
        hosting: selectedHosting,
        onMediaViewerOpen: onMediaViewerOpen,
        onWatchAppToggle: onWatchAppToggle,
        onArtifactDownloadClick: onArtifactDownloadClick,
        onCloudMigrationResourceLinksClick: onCloudMigrationResourceLinksClick,
        isServerAppEos: isServerAppEos
      })
    }),
    testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Overview),
    key: AppListingTab.Overview
  });

  // Reviews Tab
  if (showReviewsTab) {
    tabs.push({
      label: 'Reviews',
      key: AppListingTab.Reviews,
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Reviews),
      content: showAetherAppListingPage ? /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
        children: /*#__PURE__*/_jsx(LazyReviewsTabV2, {
          appKey: appListing.appKey,
          mpacBaseUrl: mpacBaseUrl,
          hosting: selectedHosting,
          vendorName: appListing.partner.name,
          vendorLogo: appListing.partner.logo,
          onSeeMoreReviewsClick: onSeeMoreReviewsClick,
          user: user,
          loginHref: loginHref,
          appRating: appRating,
          cloudCompatibleProducts: appListing.cloudCompatibleProducts,
          appDeploymentType: appListing.deploymentType,
          productHostingOptions: appListing.productHostingOptions,
          isServerAppEos: isServerAppEos
        })
      }) : /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
        children: /*#__PURE__*/_jsx(LazyReviewsTab, {
          appKey: appListing.appKey,
          mpacBaseUrl: mpacBaseUrl,
          hosting: selectedHosting,
          vendorName: appListing.partner.name,
          vendorLogo: appListing.partner.logo,
          onSeeMoreReviewsClick: onSeeMoreReviewsClick,
          user: user,
          loginHref: loginHref,
          appRating: appRating,
          cloudCompatibleProducts: appListing.cloudCompatibleProducts,
          appDeploymentType: appListing.deploymentType,
          productHostingOptions: appListing.productHostingOptions,
          isServerAppEos: isServerAppEos
        })
      })
    });
  }

  // Pricing Tab
  if (showPricingTab && appListing.paymentModel && appListing.paymentModel !== MarketplaceAppPaymentModel.Free && !isServerAppEos) {
    tabs.push({
      label: 'Pricing',
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
        children: showAetherAppListingPage ? /*#__PURE__*/_jsx(LazyPricingTabV2, {
          appId: appListing.appId,
          appKey: appListing.appKey,
          compatibleProducts: appListing.compatibleProducts,
          hasDataCenterVersion: appListing.hasDataCenterVersion,
          hosting: selectedHosting,
          learnMoreUrl: appListing.learnMoreUrl,
          marketingLabels: appListing.marketingLabels,
          partnerType: appListing.partner.partnerType,
          paymentModel: appListing.paymentModel,
          mpacBaseUrl: mpacBaseUrl,
          onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick,
          showServerEolMessage: showServerEolMessage,
          serverEolStartDate: serverEolStartDate,
          isEditionsSupported: isEditionsSupported
        }) : /*#__PURE__*/_jsx(LazyPricingTab, {
          appId: appListing.appId,
          appKey: appListing.appKey,
          compatibleProducts: appListing.compatibleProducts,
          hasDataCenterVersion: appListing.hasDataCenterVersion,
          hosting: selectedHosting,
          learnMoreUrl: appListing.learnMoreUrl,
          marketingLabels: appListing.marketingLabels,
          partnerType: appListing.partner.partnerType,
          paymentModel: appListing.paymentModel,
          mpacBaseUrl: mpacBaseUrl,
          onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick,
          showServerEolMessage: showServerEolMessage,
          serverEolStartDate: serverEolStartDate
        })
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Pricing),
      key: AppListingTab.Pricing
    });
  }

  // Privacy and Security Tab
  if (showAppListingTrustTab && !isServerAppEos) {
    var _appListing$partner;
    const {
      emailId
    } = appListing.supportDetails.contact;
    tabs.push(_objectSpread({
      label: 'Privacy & Security',
      content: /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
        children: trustTabPrecondition ? /*#__PURE__*/_jsx(LazyCustomerPrivacyAndSecurityTab, {
          mpacBaseUrl: mpacBaseUrl,
          mpacCdnBaseUrl: mpacCdnBaseUrl,
          appKey: appListing.appKey,
          appId: appListing.appId,
          appName: appListing.name,
          feedbackCollectorApiKeys: feedbackCollectorApiKeys,
          feedbackGatewayBaseUrl: feedbackGatewayBaseUrl,
          userId: user.userId,
          supportEmailId: emailId
        }) : showAetherAppListingPage ? /*#__PURE__*/_jsx(LazyPrivacySecurityDetailsNotAvailableBannerV2, {
          isHostingCloud: selectedHosting === HostingQueryParamToHostingType.cloud,
          isWorkflowApp: appListing.deploymentType === AppDeployment.WORKFLOW,
          appName: appListing.name,
          appKey: appListing.appKey,
          supportEmailId: emailId,
          mpacBaseUrl: mpacBaseUrl,
          partnerId: (_appListing$partner = appListing.partner) === null || _appListing$partner === void 0 ? void 0 : _appListing$partner.id
        }) : /*#__PURE__*/_jsx(LazyPrivacySecurityDetailsNotAvailableBanner, {
          isHostingCloud: selectedHosting === HostingQueryParamToHostingType.cloud
        })
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.PrivacyAndSecurity),
      key: AppListingTab.PrivacyAndSecurity
    }, !showAetherAppListingPage && {
      lozenge: {
        label: 'new',
        appearance: 'new'
      }
    }));
  }

  // Support Tab
  if (!isServerAppEos) {
    tabs.push({
      label: 'Support',
      content: showAetherAppListingPage ? /*#__PURE__*/_jsx(SupportTabV2, {
        isEditionsSupported: isEditionsSupported,
        onTabSelect: onTabSelect,
        appKey: appListing.appKey,
        atlassianCommunityBaseUrl: atlassianCommunityBaseUrl,
        cloudMigrationInfo: cloudMigration,
        hosting: selectedHosting,
        mpacBaseUrl: mpacBaseUrl,
        onCloudMigrationResourceLinksClick: onCloudMigrationResourceLinksClick,
        onSupportLinkClick: onSupportLinkClick,
        supportDetails: appListing.supportDetails,
        appPricingDetailsLink: appPricingDetailsLink
      }) : /*#__PURE__*/_jsx(SupportTab, {
        supportDetails: appListing.supportDetails,
        mpacBaseUrl: mpacBaseUrl,
        atlassianCommunityBaseUrl: atlassianCommunityBaseUrl,
        hosting: selectedHosting,
        appKey: appListing.appKey,
        onSupportLinkClick: onSupportLinkClick,
        showServerEolMessage: showServerEolMessage
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Support),
      key: AppListingTab.Support
    });
  }

  // Versions Tab
  if (showVersionsTab && !showAetherAppListingPage) {
    tabs.push({
      label: 'Versions',
      content: /*#__PURE__*/_jsx(VersionsTab, {
        versionHistoryUrl: appListing.versionHistoryUrl,
        hosting: selectedHosting,
        releaseDate: appListing.releaseDate,
        releaseNotes: appListing.releaseNotes,
        releaseSummary: appListing.releaseSummary,
        releaseNotesUrl: appListing.releaseNotesUrl,
        version: appListing.version,
        versionCount: appListing.versionCount,
        productCompatibilities: appListing.compatibleProducts,
        paymentModel: appListing.paymentModel,
        licenseType: appListing.licenseType
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Versions),
      key: AppListingTab.Versions
    });
  }

  // Installation Tab
  if (showInstallationTab) {
    tabs.push({
      label: 'Installation',
      content: showAetherAppListingPage ? /*#__PURE__*/_jsx(LazySuspense, {
        fallback: /*#__PURE__*/_jsx(LoadingSuspenseSpinner, {}),
        children: /*#__PURE__*/_jsx(LasyInstallationTab, {
          appName: appListing.name,
          compatibleProducts: appListing.compatibleProducts,
          deploymentType: appListing.deploymentType,
          documentationUrl: appListing.documentationUrl,
          hosting: selectedHosting,
          installationInstructions: appListing.installationInstructions,
          paymentModel: appListing.paymentModel,
          versionCount: appListing.versionCount,
          versionHistoryUrl: appListing.versionHistoryUrl
        })
      }) : /*#__PURE__*/_jsx(InstallationTab, {
        appName: appListing.name,
        compatibleProducts: appListing.compatibleProducts,
        deploymentType: appListing.deploymentType,
        documentationUrl: appListing.documentationUrl,
        hosting: selectedHosting,
        installationInstructions: appListing.installationInstructions,
        paymentModel: appListing.paymentModel,
        versionCount: appListing.versionCount,
        versionHistoryUrl: appListing.versionHistoryUrl
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Installation),
      key: AppListingTab.Installation
    });
  }
  return tabs;
};